import { Component, inject, input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { Observable, Subscription } from 'rxjs';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import {
  HtmlCheckboxType,
  HtmlSwitchSize,
  ZiroCheckboxConfig,
} from '../../../../forms/fields/checkbox/ziro-checkbox.component';
import { MobilityIdentity, MobilityIdentityFieldConfig } from '../../../../shared/models/generated/ziroModels';
import { PhoneUiContext } from '../../../../shared/phone-buttons/contexts/phone-ui.context';
import { TranslateService } from '@ngx-translate/core';
import { filter, first } from 'rxjs/operators';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';

export interface MobilityIdentityFormdata {
  id: string | null;
  formSwitch: boolean;
  name: string;
  destination: string;
  delayBeforeRingingInSeconds: number;
  answerTooLateTimerInSeconds: number;
  answerTooSoonTimerInSeconds: number;
}

@Component({
  selector: 'ziro-phone-mobility-identity',
  templateUrl: './phone-mobility-identity.component.html',
  standalone: false,
})
export class PhoneMobilityIdentityComponent
  extends ZiroFormAbstractDirective<MobilityIdentityFormdata>
  implements OnInit, OnChanges, OnDestroy
{
  private phoneUiContext = inject(PhoneUiContext);
  private translateService = inject(TranslateService);

  readonly fieldConfig = input<MobilityIdentityFieldConfig>();
  readonly isExistingMobilityIdentity = input<boolean>();
  ziroIcons = ZiroIcons;
  formConfig: ZiroFormConfig;
  isFormSwitchChecked: boolean;

  private _validators = {
    nameFieldValidator: (input: string) =>
      /^[^/%&<>"]+$/.test(input) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
    destinationValidator: (input: string) =>
      /^[0-9*+#]+$/.test(input) ||
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        input,
      )
        ? ZiroFormsValidationState.VALID
        : ZiroFormsValidationState.INVALID,
    beforeRingValidator: (val: number) =>
      val >= 0 && val <= 30 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
    answerTooSoonValidator: (val: number) =>
      val >= 0 && val <= 10 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
    answerTooLateValidator: (val: number) =>
      (val >= 10 && val <= 300) || val == 0 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
  };
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.isFormSwitchChecked = this.isExistingMobilityIdentity();
    this.formConfig = {
      fields: {
        formSwitch: {
          label: 'tkey;pages.details.phoneSettings.mobility_identity',
          dataAutomation: 'mobility-identity-switch',
          componentConfig: new ZiroCheckboxConfig({
            checkboxType: HtmlCheckboxType.SWITCH,
            size: HtmlSwitchSize.LG,
          }),
        },
        name: {
          label: 'tkey;pages.details.phoneSettings.mobility_identity.name',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          dataAutomation: 'mobility-identity-name',
          required: () =>
            (this.isExistingMobilityIdentity() && this.fieldConfig()?.name.required) ||
            (this.isExistingMobilityIdentity() && !this.fieldConfig()) ||
            this.fieldConfig()?.name.required,
          hidden: () => !this.isFormSwitchChecked || (this.isFormSwitchChecked && !this.fieldConfig()?.name.show),
          validation: [
            {
              validator: this._validators.nameFieldValidator,
              message: 'tkey;validators.global.error.pattern',
            },
            {
              validator: (val: string) => {
                return val.length > 50 ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: this.translateService.instant('tkey;validators.global.error.maxlength', {
                maxlength: 50,
              }),
            },
          ],
          defaultValue: () => this.fieldConfig().name.defaultValue,
        },
        destination: {
          label: 'tkey;pages.details.phoneSettings.mobility_identity.destination',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          dataAutomation: 'mobility-identity-destination',
          required: () =>
            (this.isExistingMobilityIdentity() && this.fieldConfig()?.destination.required) ||
            (this.isExistingMobilityIdentity() && !this.fieldConfig()) ||
            this.fieldConfig()?.destination.required,
          hidden: () =>
            !this.isFormSwitchChecked || (this.isFormSwitchChecked && !this.fieldConfig()?.destination.show),
          validation: [
            {
              validator: (val: string) => {
                return val.length > 24 ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: this.translateService.instant('tkey;validators.global.error.maxlength', {
                maxlength: 24,
              }),
            },
            {
              validator: this._validators.destinationValidator,
              message: 'tkey;validators.mobility_identity_destination.invalid.error',
            },
          ],
          defaultValue: () => this.fieldConfig().destination.defaultValue,
        },
        delayBeforeRingingInSeconds: {
          label: 'tkey;pages.details.phoneSettings.mobility_identity.delay_before_ring',
          labelToolTipIconClass: this.ziroIcons.TOOLTIP,
          labelToolTipText: 'tkey;mobility_identity.delay_before_ring.tooltip',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER, step: 0.1 }),
          dataAutomation: 'mobility-identity-delay-before-ringing',
          required: () =>
            (this.isExistingMobilityIdentity() && this.fieldConfig()?.delayBeforeRingingInSeconds.required) ||
            (this.isExistingMobilityIdentity() && !this.fieldConfig()) ||
            this.fieldConfig()?.delayBeforeRingingInSeconds.required,
          hidden: () =>
            !this.isFormSwitchChecked ||
            (this.isFormSwitchChecked && !this.fieldConfig()?.delayBeforeRingingInSeconds.show),
          validation: [
            {
              validator: this._validators.beforeRingValidator,
              message: 'tkey;pages.details.phoneSettings.mobility_identity.delay_before_ring.range_error',
            },
          ],
          defaultValue: () => Number(this.fieldConfig().delayBeforeRingingInSeconds.defaultValue),
        },
        answerTooSoonTimerInSeconds: {
          label: 'tkey;pages.details.phoneSettings.mobility_identity.answer_too_soon_timer_in_seconds.label',
          labelToolTipIconClass: this.ziroIcons.TOOLTIP,
          labelToolTipText: 'tkey;mobility_identity.answer_too_soon.tooltip',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER, step: 0.1 }),
          dataAutomation: 'answer-too-soon-timer-in-seconds',
          required: () =>
            (this.isExistingMobilityIdentity() && this.fieldConfig()?.answerTooSoonTimerInSeconds.required) ||
            (this.isExistingMobilityIdentity() && !this.fieldConfig()) ||
            this.fieldConfig()?.answerTooSoonTimerInSeconds.required,
          hidden: () =>
            !this.isFormSwitchChecked ||
            (this.isFormSwitchChecked && !this.fieldConfig()?.answerTooSoonTimerInSeconds.show),
          validation: [
            {
              validator: this._validators.answerTooSoonValidator,
              message:
                'tkey;pages.details.phoneSettings.mobility_identity.answer_too_soon_timer_in_seconds.range_error',
            },
          ],
          defaultValue: () => Number(this.fieldConfig().answerTooSoonTimerInSeconds.defaultValue),
        },
        answerTooLateTimerInSeconds: {
          label: 'tkey;pages.details.phoneSettings.mobility_identity.answer_too_late_timer_in_seconds.label',
          labelToolTipIconClass: this.ziroIcons.TOOLTIP,
          labelToolTipText: 'tkey;mobility_identity.answer_too_late.tooltip',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER, step: 0.1 }),
          dataAutomation: 'answer-too-late-timer-in-seconds',
          required: () =>
            (this.isExistingMobilityIdentity() && this.fieldConfig()?.answerTooLateTimerInSeconds.required) ||
            (this.isExistingMobilityIdentity() && !this.fieldConfig()) ||
            this.fieldConfig()?.answerTooLateTimerInSeconds.required,
          hidden: () =>
            !this.isFormSwitchChecked ||
            (this.isFormSwitchChecked && !this.fieldConfig()?.answerTooLateTimerInSeconds.show),
          validation: [
            {
              validator: this._validators.answerTooLateValidator,
              message:
                'tkey;pages.details.phoneSettings.mobility_identity.answer_too_late_timer_in_seconds.range_error',
            },
          ],
          defaultValue: () => Number(this.fieldConfig().answerTooLateTimerInSeconds.defaultValue),
        },
      },
    };

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((update) => {
      this.isFormSwitchChecked = update.new.formSwitch;
      this.fieldComponents.forEach((fieldComponent) => {
        if (fieldComponent.config.componentConfig instanceof ZiroTextConfig) {
          this.fieldChannels[fieldComponent.fieldId()].stateSource.next({
            ...fieldComponent.state,
            hidden: fieldComponent.config.hidden() ?? !this.isFormSwitchChecked,
            required: this.isFormSwitchChecked
              ? typeof fieldComponent.config.required === 'function' && fieldComponent.config.required()
              : false,
            defaultValue: this.isFormSwitchChecked ? this._getFieldDefault(fieldComponent.fieldId()) : null,
          });
        }
      });
    });
    this._subscriptions.add(formUpdateSub);

    const phoneSubmitSub = this.phoneUiContext.isSaving$.subscribe((isSubmitting) => {
      this.fieldComponents.forEach((fieldComponent) => {
        fieldComponent.applyState({
          ...fieldComponent.state,
          disabled: isSubmitting,
        });
      });
    });
    this._subscriptions.add(phoneSubmitSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    super.ngOnDestroy();
  }

  getMobilityIdentity(): MobilityIdentity {
    return {
      id: this.formData.id,
      name: this.formData.name,
      destination: this.formData.destination,
      delayBeforeRingingInSeconds: this.formData.delayBeforeRingingInSeconds,
      answerTooLateTimerInSeconds: this.formData.answerTooLateTimerInSeconds,
      answerTooSoonTimerInSeconds: this.formData.answerTooSoonTimerInSeconds,
    };
  }

  getIsFormDirty(): boolean {
    return this._ziroFormStateService.getIsFormDirty();
  }

  protected submit(): Observable<boolean> {
    return this.phoneUiContext.isSaving$.pipe(
      filter((isSaving) => !isSaving),
      first(),
    );
  }

  private _getFieldDefault(fieldId: string): string | number {
    switch (fieldId) {
      case 'name':
        return this.fieldConfig().name.defaultValue;
      case 'destination':
        return this.fieldConfig().destination.defaultValue;
      case 'delayBeforeRingingInSeconds':
        return Number(this.fieldConfig().delayBeforeRingingInSeconds.defaultValue);
      case 'answerTooSoonTimerInSeconds':
        return Number(this.fieldConfig().answerTooSoonTimerInSeconds.defaultValue);
      case 'answerTooLateTimerInSeconds':
        return Number(this.fieldConfig().answerTooLateTimerInSeconds.defaultValue);
    }
  }
}
